import type { de } from './de';

/** English texts, with the same keys as the German ones. */
export const en: typeof de = {
  name: 'Occupational safety',
  description:
    'Workplace inspections of the occupational safety specialist: defects with photos, general information, attendees, report and a search for hazardous substances in GESTIS.',
  nav: { inspections: 'Inspections', substances: 'Hazardous substances' },
  permissions: {
    inspection: {
      read: {
        label: 'View inspections',
        description:
          'Open inspections with defects, attachments and history and create reports. With scope “Own” only inspections the person inspects or created.',
      },
      write: {
        label: 'Edit inspections',
        description:
          'Create, change, start, complete and reopen inspections, record defects with photos and attachments. With scope “Own” only inspections the person inspects or created.',
      },
      delete: {
        label: 'Delete inspections',
        description: 'Delete inspections with their defects, attachments and comments.',
      },
      comment: {
        label: 'Comment on inspections',
        description: 'Write comments on inspections.',
      },
      export: {
        label: 'Export inspections',
        description: 'Download the list of inspections as a CSV file.',
      },
    },
    substance: {
      search: {
        label: 'Search hazardous substances',
        description:
          'Search substances in the GESTIS substance database. The search runs live, nothing is stored.',
      },
    },
    settings: {
      manage: {
        label: 'Set up occupational safety',
        description:
          'Maintain the defect catalog, report templates, questionnaires and settings, save views of the inspection list for the company.',
      },
    },
  },
  roles: {
    inspector: 'Occupational safety: Carry out inspections',
    manager: 'Occupational safety: Manage inspections',
  },
  settings: {
    offlineDays: {
      label: 'Completed inspections offline (days)',
      description:
        'Own inspections stay on the device for this many days after “Report sent”. With 0 only planned and running ones.',
    },
  },
  states: { planned: 'Planned', 'in-progress': 'Running', completed: 'Completed' },
  offline: { inspections: 'Safety inspections' },
  ai: { findingDraft: 'Draft a defect from notes' },
  questionnaires: { general: 'General information' },
  documents: {
    inspectionReport: 'Safety inspection report',
    groups: {
      project: 'Project',
      object: 'Object',
      customer: 'Customer',
      inspection: 'Inspection',
      findings: 'Defects',
    },
    placeholders: {
      project: { number: 'Project number', title: 'Project title' },
      object: {
        name: 'Name',
        number: 'Object number',
        customerNumber: 'Customer’s object number',
        address: 'Address',
        street: 'Street and number',
        postalCode: 'Postcode',
        city: 'City',
        state: 'Federal state',
        usage: 'Use',
        buildingClass: 'Building class',
      },
      customer: {
        name: 'Name',
        number: 'Customer number',
        address: 'Address',
        phone: 'Phone',
        email: 'Email',
        website: 'Website',
        contact: 'Contact person',
      },
      inspection: {
        title: 'Title',
        date: 'Date',
        inspectors: 'Inspectors',
        participants: 'Participants',
        attendees: 'Attendees',
        status: 'Status',
        remarks: 'Remarks',
      },
      findings: { count: 'Number of defects', open: 'Open defects' },
    },
    sources: { attendees: 'Attendees', remarks: 'Remarks' },
    reportTitle: 'Safety inspection report {{title}} {{date}}',
    columns: { name: 'Name', role: 'Role' },
    checks: {
      unanswered: 'General information: {{count}} questions without an answer.',
      attendees: 'No attendees entered.',
      remarks: 'No remarks entered.',
    },
    sample: {
      title: 'Inspection 08.10.2026',
      project: 'Occupational safety care 2026',
      object: 'Plant Sampletown',
      customer: 'Sample Manufacturing Ltd',
      participants: 'Works council, company doctor',
      siteManager: 'Managing director',
      foreman: 'Safety officer',
      remarks: 'Workshop tidy, escape routes clear.',
    },
  },
  audit: {
    inspection: {
      create: 'Inspection created',
      update: 'Inspection changed',
      start: 'Inspection started',
      complete: 'Report sent',
      reopen: 'Inspection reopened',
      delete: 'Inspection deleted',
    },
  },
  guides: {
    inspections: {
      title: 'Safety inspections',
      summary: 'Plan, carry out and complete workplace inspections, and who may do what.',
    },
    substances: {
      title: 'Search hazardous substances',
      summary: 'Find substances with their data sheet in GESTIS without storing anything.',
    },
  },
  errors: {
    project: 'Choose a project of your own company.',
    projectArchived: 'The project is archived. Choose a running project.',
    object: 'Choose an object of the project that is not archived.',
    title: 'Enter a title (at most 200 characters).',
    date: 'Enter a valid date.',
    status: 'A new inspection starts as planned or running.',
    inspector: 'Choose active accounts of the company as inspectors.',
    tooManyInspectors: 'An inspection has at most 20 inspectors.',
    participants: 'Participants may be at most 2000 characters long.',
    attendeeName: 'Enter a name (at most 200 characters).',
    attendeeRole: 'The function may be at most 200 characters long.',
    tooManyAttendees: 'An inspection has at most 100 attendees.',
    remarks: 'Remarks may be at most 20,000 characters long.',
    attachment: 'Attachments must be PDF files of this inspection, each only once.',
    attachmentTitle: 'The title of an attachment may be at most 200 characters long.',
    tooManyAttachments: 'An inspection has at most 50 attachments.',
  },
  substances: {
    title: 'Hazardous substances',
    description:
      'Search substances in the GESTIS substance database of the Institute for Occupational Safety and Health of the DGUV (IFA), with classification, limit values and protective measures. The search runs live; the app stores nothing.',
    query: 'Substance',
    queryHint: 'Substance name, CAS or EC number.',
    queryPlaceholder: 'e.g. acetone or 67-64-1',
    language: 'Language',
    languages: { de: 'German', en: 'English' },
    search: 'Search',
    start: 'Enter a search term',
    startHint: 'The search asks GESTIS; the data sheet opens there.',
    offline: 'The search needs a connection. It asks GESTIS live.',
    notice:
      'GESTIS describes pure substances. For a product, the safety data sheet of the supplier is what counts (section 6 (2) GefStoffV). The app sends only the search term, no names.',
    newTab: '(opens in a new tab)',
    results: 'Substances in GESTIS',
    resultsHint: 'At most 25 hits.',
    name: 'Substance',
    cas: 'CAS number',
    open: 'Open data sheet',
    none: 'No substance found',
    noneHint: 'Try another name or the CAS number.',
    errors: {
      tooShort: 'Please enter at least two characters.',
      invalid: 'Please check the search term.',
      busy: 'Too many searches in a short time. Please wait a minute.',
      unavailable: 'GESTIS is not answering right now. Please search again later.',
      forbidden: 'You lack the permission for this.',
    },
  },
  sections: { inspections: 'Safety inspections' },
  pages: {
    unavailable: 'not available',
    offlinePerson: 'Name only with a connection',
    errors: {
      versionConflict:
        'The inspection was changed in the meantime. The page now shows the new state; please repeat your change.',
      completed: 'The report has been sent. Reopen the inspection to change anything.',
      status: 'This step does not fit the status of the inspection. Please reload the page.',
      invalidInput: 'Please check your entries.',
      notFound: 'The inspection no longer exists.',
      forbidden: 'You are not allowed to do this.',
    },
    list: {
      title: 'Inspections',
      description: 'Occupational safety inspections at the sites the company looks after.',
      search: 'Search inspections',
      searchPlaceholder: 'Title, project or object',
      exportName: 'Safety inspections',
      offline: 'No connection: inspections on this device',
      allStatuses: 'All statuses',
      allProjects: 'All projects',
      allObjects: 'All objects',
      allInspectors: 'All inspectors',
      from: 'Date from',
      to: 'Date to',
      noMatches: 'No matching inspections',
      noMatchesHint: 'Change the search or the filters.',
      empty: 'No inspections yet',
      emptyHint: 'Create the first inspection with "Start inspection".',
      emptyReadOnly: 'Inspections show here once they are created.',
      findings: '{{open}} / {{total}}',
      columns: {
        title: 'Title',
        status: 'Status',
        project: 'Project',
        object: 'Object',
        date: 'Date',
        inspectors: 'Inspectors',
        findings: 'Defects open / total',
      },
    },
    section: {
      date: 'Date',
      title: 'Title',
      object: 'Object',
      status: 'Status',
      openFindings: 'Open defects',
      empty: 'No Safety inspections yet',
      emptyHint: 'Create the first one here with “Start inspection”.',
      emptyReadOnly: 'Safety inspections show here once they are created.',
    },
    start: {
      action: 'Start inspection',
      title: 'Start inspection',
      planTitle: 'Plan inspection',
      description: 'With a date in the future the inspection is planned.',
      submit: 'Start',
      plan: 'Plan',
      started: 'Inspection started',
      planned: 'Inspection planned',
      copyFailed: 'The inspection was created, but the defects could not be taken over.',
      offlineUnavailable:
        'Without a connection inspections can only be created when the device keeps Safety inspections offline.',
      object: 'Object',
      objectPlaceholder: 'Choose an object on this device',
      objectHint: 'The site. Project and customer follow from the object.',
      projectObjectHint: 'One of the objects of the project.',
      projectObjectPlaceholder: 'Choose an object of the project',
      noObjects: 'The project has no objects',
      noObjectsHint:
        'An inspection takes place at an object. Add an object to the project under “Objects”, then the inspection can start.',
      projectClosed: 'Project not open',
      projectClosedHint:
        'Inspections start only in open projects of your own company, not in closed, cancelled or archived ones.',
      project: 'Project',
      projectLoading: 'Finding the project',
      noProject: 'No open project at this object',
      noProjectHint:
        'An inspection always belongs to a project. Add the object to a running project under Projects, then the inspection can start.',
      projectChoice: 'Several open projects cover the object. Which one is the inspection part of?',
      projectPlaceholder: 'Choose a project',
      projectRequired: 'Please choose the project of the inspection.',
      otherProject: 'Choose another project',
      customer: 'Customer',
      noCustomer: 'The project has no client.',
      titleField: 'Title',
      titleHint: 'Without one "Begehung" with the date.',
      date: 'Date',
      dateHint: 'A date in the future plans the inspection.',
      template: 'General information',
      templatesUnavailable:
        'The templates of the general information are not on this device. The inspection starts without a questionnaire.',
      copy: 'Take over defects from an earlier inspection',
      copyFrom: 'Earlier inspection',
      copyNone: 'Take over none',
      closedFinding: 'done',
      positiveFinding: 'positive',
      copyOffline: 'Defects can only be taken over with a connection.',
    },
    detail: {
      notFound: 'Inspection not found',
      notFoundHint: 'It was deleted, or you lack the permission.',
      notOnDevice: 'Inspection not on this device',
      notOnDeviceHint:
        'Without a connection the app opens only inspections on the device. Open it once with a connection first.',
      onlineOnly: 'Not available without a connection',
      onlineOnlyHint:
        'Your device keeps nothing offline in this company, for example because you work as a superadmin in a company other than your own.',
      steps: {
        start: 'Start',
        complete: 'Report sent',
        reopen: 'Reopen',
        startDone: 'Inspection started',
        completeDone: 'Marked as sent',
        reopenDone: 'Inspection reopened',
      },
      deleted: '"{{title}}" deleted',
      offlineRefused: 'A change on this device was refused',
      offlineRefusedHint:
        'Reason: {{reason}}. Details and discarding in the "Sync" window in the top bar.',
      completedTitle: 'Report sent',
      completedSince:
        'Since {{when}}. Information and attachments change only after "Reopen"; comments still work.',
      plansHint: 'Plans of the project and the object.',
      projectPlans: 'Plans of the project',
      objectPlans: 'Plans of the object',
      createReport: 'Create report',
      copySource: '{{date}}: {{title}}',
      noFinding: 'In the view of the AI this is not a defect: {{reason}}',
      noPositive: 'In the view of the AI this is nothing positive: {{reason}}',
      deleteTitle: 'Delete inspection?',
      deleteDescription: '"{{title}}" will be deleted.',
      deleteHeavy:
        '"{{title}}" will be deleted with {{findings}} defects, {{files}} files and the comments. This cannot be undone.',
      deleteWord: 'DELETE',
      deleteWordLabel: 'Type DELETE to confirm',
      questionnaire: 'General information',
      noQuestionnaire: 'No general information.',
      attendees: 'Attendees',
      attendeeName: 'Name',
      attendeeRole: 'Function',
      addAttendee: 'Add person',
      attendeeRow: 'Attendee {{number}}',
      removeAttendee: 'Remove attendee {{number}}',
      noAttendees: 'No attendees yet.',
      none: 'Not given.',
      notes: 'Participants and remarks',
      participants: 'Participants',
      participantsHint: 'Further participants, e.g. works council or company doctor.',
      remarks: 'Remarks',
      reportAttachments: 'Attachments of the report',
      reportAttachmentsHint: 'PDF files of the inspection that follow the report, in this order.',
      noReportAttachments: 'No attachments in the report yet.',
      attachmentNumber: 'Attachment {{number}}',
      moveUp: 'Move "{{name}}" up',
      moveDown: 'Move "{{name}}" down',
      removeFromReport: 'Take "{{name}}" out of the report',
      addToReport: 'Attach "{{name}}"',
      editTitle: 'Edit inspection',
      inspectors: 'Inspectors',
      inspectorsOffline: 'Inspectors can only be changed with a connection.',
      sections: 'Sections of the inspection',
      tabs: {
        findings: 'Defects',
        questionnaire: 'Questionnaire',
        protocol: 'Minutes',
        plans: 'Plans',
        files: 'Files',
        history: 'History',
      },
      openFindings: '{{count}} open',
      meta: {
        date: 'Date',
        object: 'Object',
        project: 'Project',
        customer: 'Customer',
        inspectors: 'Inspectors',
      },
    },
  },
};
